import "../styles/globals.css";
import { ThemeProvider } from "next-themes";
import MainLayout from "../components/MainLayout.jsx";
import DashboardLayout from "../components/dashboardLayout.jsx";
import { Provider } from "react-redux";
import { store } from "../redux/store";
import { useRouter } from "next/router";
import Meta from "../components/Meta";
import UserContext from "../components/UserContext";
import { useRef, useEffect, useMemo, memo } from "react";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer } from "react-toastify";
import LoadingOverlay from "./LoadingOverlay";
import { pageview } from "../utils/analytics";
import { AuthProvider } from "../contexts/AuthContext";
import dynamic from "next/dynamic";
import withAuth from "../components/withAuth";
import {
  shouldSkipAllLayouts,
  isStandaloneDashboardPage,
  isDashboardPage,
} from "../config/layoutConfig";
import RoleGuard from "../components/roles/RoleGuard";
import { RoleProvider } from "../contexts/RoleContext";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

// --- Wagmi Imports ---
import { WagmiProvider, createConfig, http } from "wagmi";
import { polygon } from "wagmi/chains";
import { coinbaseWallet } from "wagmi/connectors";

// --- Lazy load NFTMarketplaceProvider (bundle-dynamic-imports) ---
const NFTMarketplaceProvider = dynamic(
  () =>
    import("../contexts/NFTMarketplaceContext").then(
      (mod) => mod.NFTMarketplaceProvider
    ),
  { ssr: false }
);

// --- Wagmi Configuration ---
const wagmiConfig = createConfig({
  chains: [polygon],
  connectors: [
    coinbaseWallet({
      appName: "Impactoverse",
    }),
  ],
  transports: {
    [polygon.id]: http(),
  },
});

// Create QueryClient with optimized config for performance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

if (typeof window !== "undefined") {
  require("bootstrap/dist/js/bootstrap");
}

// --- Memoized Core Providers (rerender-memo) ---
const CoreProviders = memo(function CoreProviders({ children }) {
  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <WagmiProvider config={wagmiConfig}>
          <AuthProvider>
            <RoleProvider>{children}</RoleProvider>
          </AuthProvider>
        </WagmiProvider>
      </QueryClientProvider>
    </Provider>
  );
});

// --- Memoized Theme Wrapper ---
const ThemeWrapper = memo(function ThemeWrapper({ children, scrollRef }) {
  return (
    <ThemeProvider enableSystem={true} attribute="class">
      <ToastContainer />
      <UserContext.Provider value={{ scrollRef }}>
        {children}
        <LoadingOverlay />
      </UserContext.Provider>
    </ThemeProvider>
  );
});

// --- Pages that need NFT context (memoized check) ---
// Include dashboard routes since components may use NFT marketplace context
const NFT_ROUTES = [
  "/marketplace",
  "/nft",
  "/create",
  "/dashboard",
  "/asset",
  // Public collection pages gate "Buy with card" on wallet connection
  "/collection",
];

function useNeedsNFTContext(pathname) {
  return useMemo(() => {
    return NFT_ROUTES.some(
      (route) => pathname === route || pathname.startsWith(`${route}/`)
    );
  }, [pathname]);
}

function MyApp({ Component, pageProps }) {
  const router = useRouter();
  const pid = router.asPath;
  const scrollRef = useRef({
    scrollPos: 0,
  });

  useEffect(() => {
    const handleRouteChange = (url) => {
      pageview(url);
    };

    pageview(router.asPath);

    router.events.on("routeChangeComplete", handleRouteChange);
    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [router.asPath, router.events]);

  // Memoized page config checks
  const shouldSkipLayout = useMemo(() => shouldSkipAllLayouts(pid), [pid]);
  const isStandalone = useMemo(() => isStandaloneDashboardPage(pid), [pid]);
  const isDashboard = useMemo(() => isDashboardPage(pid), [pid]);

  // Check if we're on an auth page that doesn't need heavy providers
  const isAuthPage = useMemo(() => {
    return [
      "/sign-in",
      "/sign-up",
      "/forget-password",
      "/new-password",
      "/confirm-email",
      "/email-confirmed",
    ].some((path) => pid.startsWith(path));
  }, [pid]);

  // Check if page needs NFT context (bundle-defer-third-party)
  const needsNFTContext = useNeedsNFTContext(router.pathname);

  let renderedComponent;

  if (shouldSkipLayout) {
    renderedComponent = <Component {...pageProps} />;
  } else if (isStandalone) {
    // Standalone dashboard pages need authentication but no dashboard layout
    const ProtectedComponent = withAuth(Component);
    renderedComponent = (
      <RoleGuard>
        <ProtectedComponent {...pageProps} />
      </RoleGuard>
    );
  } else if (isDashboard) {
    // Wrap dashboard pages with DashboardLayout and auth
    const ProtectedComponent = withAuth(Component);
    renderedComponent = (
      <DashboardLayout>
        <ProtectedComponent {...pageProps} />
      </DashboardLayout>
    );
  } else {
    renderedComponent = (
      <MainLayout>
        <Component {...pageProps} />
      </MainLayout>
    );
  }

  // For auth pages, use minimal providers (no NFT context)
  if (isAuthPage) {
    return (
      <>
        <Meta title="Home" />
        <CoreProviders>
          <ThemeWrapper scrollRef={scrollRef}>{renderedComponent}</ThemeWrapper>
        </CoreProviders>
      </>
    );
  }

  // For pages that need NFT context, include it
  // Otherwise, conditionally load based on route (bundle-defer-third-party)
  return (
    <>
      <Meta title="Home" />
      <CoreProviders>
        {needsNFTContext ? (
          <NFTMarketplaceProvider>
            <ThemeWrapper scrollRef={scrollRef}>
              {renderedComponent}
            </ThemeWrapper>
          </NFTMarketplaceProvider>
        ) : (
          <ThemeWrapper scrollRef={scrollRef}>{renderedComponent}</ThemeWrapper>
        )}
      </CoreProviders>
    </>
  );
}

export default MyApp;
